import { useEffect } from "react";

import { filter, some, sortBy } from "lodash/fp";
import { matchPath, useLocation, useSearchParams } from "react-router-dom";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";

import { isStaleFeaturePopup } from "@app/analytics/utils/deprecationUtils";
import useAuth from "@app/common/auth/useAuth";
import useLocalStorageTimeout from "@app/common/hooks/useLocalStorageTimeout";
import ROUTES from "@app/common/routes";
import Config from "@app/config";

const EXCLUDED_ROUTES = [`${ROUTES.SETTINGS_ROOT}/*`];

interface UsePopupsProps {
  // id of the popup, must be unique or an error is thrown
  id: string;
  // id of the feature associated with the popup. if passed, will be used instead
  // of the popup id to determine if the popup is "stale"
  featureId?: string;
  // lower numbers will have higher priority. if more than one popup has the same priority,
  // we will show the first popup that was registered
  priority: number;
  // if false, will not show the popup regardless of priority
  shouldShow: boolean;
  // if any popups are loading, no popups will show
  isLoading?: boolean;
  // if true, will show the popup regardless of when the last popup was shown
  force?: boolean;
}

interface PopupState {
  registry: Record<string, UsePopupsProps>;
  popupToShow: string | null;
  registerPopup: (popup: UsePopupsProps) => void;
  unregisterPopup: (id: string) => void;
}

// Get highest-priority popup based on priority and whether they should show.
// If there are multiple popups with the same priority, show first one.
// A second popup with equal priority will be shown after the "show popup" timeout expires
const getHighestPriorityId = (
  registry: Record<string, UsePopupsProps>
): string | null => {
  const popupsByPriority = filter(
    { shouldShow: true },
    sortBy("priority", Object.values(registry))
  );

  if (popupsByPriority.length > 1) {
    const [first, second] = popupsByPriority;
    if (first.priority === second.priority) {
      return first.id;
    }
  }

  return popupsByPriority.length ? popupsByPriority[0].id : null;
};

const usePopupStore = createWithEqualityFn<PopupState>(
  (set) => ({
    // map string to popup, so we can rank them if we have multiple to show
    registry: {},
    popupToShow: null,
    registerPopup: (popup: UsePopupsProps) =>
      set((state) => {
        const popupsToRank = { ...state.registry, [popup.id]: popup };

        const somePopupsLoading = some("isLoading", popupsToRank);

        // Don't do any ranking until all popups are done loading
        if (somePopupsLoading)
          return {
            registry: popupsToRank,
            popupToShow: null,
          };

        const highestPriorityId = getHighestPriorityId(popupsToRank);
        const newRegistry = highestPriorityId
          ? { [highestPriorityId]: popupsToRank[highestPriorityId] }
          : {};

        return {
          registry: newRegistry,
          popupToShow: highestPriorityId,
        };
      }),
    unregisterPopup: (id: string) =>
      set((state) => {
        if (!state.registry[id]) {
          return state;
        }
        const { [id]: _, ...newRegistry } = state.registry;
        return {
          registry: newRegistry,
          popupToShow: getHighestPriorityId(newRegistry),
        };
      }),
  }),
  shallow
);

const usePopups = (popup: UsePopupsProps) => {
  const { user } = useAuth();
  const [queryParams] = useSearchParams();
  const location = useLocation();
  const { registerPopup, unregisterPopup, popupToShow } = usePopupStore(
    (state) => ({
      registerPopup: state.registerPopup,
      unregisterPopup: state.unregisterPopup,
      popupToShow: state.popupToShow,
    })
  );

  const [lastShownDateBeforeToday, resetPopupLastShownDate] =
    useLocalStorageTimeout("lastPopupShown");

  const canShowPopup = lastShownDateBeforeToday || Boolean(popup.force);

  const hidePopups =
    !user ||
    Boolean(queryParams.get("ht")) ||
    EXCLUDED_ROUTES.some((route) => matchPath(route, location.pathname));

  // Will show this popup if the popup query param matches the popup id
  // regardless of priority or when it was last shown
  const hasForceShowParam = queryParams.get("popup_id");
  const forceShowPopup = hasForceShowParam === popup.id;

  const featureReleaseDate = popup.featureId
    ? Config.releaseDates[popup.featureId]
    : Config.releaseDates[popup.id];

  const popupIsStale = Boolean(
    featureReleaseDate && isStaleFeaturePopup(featureReleaseDate)
  );

  useEffect(() => {
    if (popupIsStale) return;

    registerPopup(popup);

    return () => {
      unregisterPopup(popup.id);
    };
  }, [
    popup.id,
    popup.priority,
    popup.shouldShow,
    popup.isLoading,
    popupIsStale,
  ]);

  return {
    show:
      forceShowPopup ||
      (!hasForceShowParam &&
        !hidePopups &&
        canShowPopup &&
        !popupIsStale &&
        popup.id === popupToShow),
    reset: resetPopupLastShownDate,
  };
};

export default usePopups;
